import React, { useCallback, useEffect, useState } from 'react';
import { Locale } from '../../types';
import type {
  ProjectDetail as ProjectDetailData,
  AdminSection,
  AdminOption,
  AdminDefaultOption,
  SectionInput,
  OptionInput,
  DefaultOptionInput,
} from '../../services/adminApi';
import {
  getProjectDetail,
  renameProject,
  upsertClient,
  createSection,
  updateSection,
  deleteSection,
  createOption,
  updateOption,
  deleteOption,
  createDefaultOption,
  updateDefaultOption,
  deleteDefaultOption,
} from '../../services/adminApi';
import SectionForm from './SectionForm';
import OptionForm from './OptionForm';
import DefaultOptionForm from './DefaultOptionForm';
import styles from './AdminCrud.module.css';

interface ProjectDetailProps {
  projectId: string;
  onBack: () => void;
}

type SectionEditing = { mode: 'new' } | { mode: 'edit'; section: AdminSection } | null;
type OptionEditing =
  | { mode: 'new'; sectionId: string }
  | { mode: 'edit'; option: AdminOption }
  | null;
type DefaultOptionEditing =
  | { mode: 'new' }
  | { mode: 'edit'; defaultOption: AdminDefaultOption }
  | null;

/** Full editor for one project: name, client credentials, sections, options */
const ProjectDetail: React.FC<ProjectDetailProps> = ({ projectId, onBack }) => {
  const [detail, setDetail] = useState<ProjectDetailData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [companyName, setCompanyName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);

  const [clientUsername, setClientUsername] = useState('');
  const [clientPassword, setClientPassword] = useState('');
  const [clientMessage, setClientMessage] = useState<string | null>(null);
  const [clientError, setClientError] = useState<string | null>(null);
  const [isSavingClient, setIsSavingClient] = useState(false);

  const [sectionEditing, setSectionEditing] = useState<SectionEditing>(null);
  const [optionEditing, setOptionEditing] = useState<OptionEditing>(null);
  const [defaultOptionEditing, setDefaultOptionEditing] =
    useState<DefaultOptionEditing>(null);

  const refresh = useCallback(async () => {
    try {
      const data = await getProjectDetail(projectId);
      setDetail(data);
      setCompanyName(data.company_name);
      setClientUsername(data.client_username ?? '');
      setError(null);
    } catch (err) {
      setError((err as Error).message || 'Failed to load project');
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    setIsLoading(true);
    refresh();
  }, [refresh]);

  const handle_rename = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!companyName.trim()) {
      return;
    }
    setIsRenaming(true);
    try {
      await renameProject(projectId, companyName.trim());
      await refresh();
    } catch (err) {
      setError((err as Error).message || 'Failed to rename project');
    } finally {
      setIsRenaming(false);
    }
  };

  const handle_save_client = async (event: React.FormEvent) => {
    event.preventDefault();
    setClientError(null);
    setClientMessage(null);

    if (!clientUsername.trim()) {
      setClientError('Username is required');
      return;
    }

    setIsSavingClient(true);
    try {
      await upsertClient(projectId, {
        username: clientUsername.trim(),
        ...(clientPassword ? { password: clientPassword } : {}),
      });
      setClientPassword('');
      setClientMessage('Client credentials saved');
      await refresh();
    } catch (err) {
      setClientError((err as Error).message || 'Failed to save client');
    } finally {
      setIsSavingClient(false);
    }
  };

  const handle_delete_section = async (section: AdminSection) => {
    const confirmed = window.confirm(
      `Delete section "${section.title.en}" and all its options?`
    );
    if (!confirmed) {
      return;
    }
    try {
      await deleteSection(section.id);
      await refresh();
    } catch (err) {
      setError((err as Error).message || 'Failed to delete section');
    }
  };

  const handle_delete_option = async (option: AdminOption) => {
    const confirmed = window.confirm(`Delete option "${option.title.en}"?`);
    if (!confirmed) {
      return;
    }
    try {
      await deleteOption(option.id);
      await refresh();
    } catch (err) {
      setError((err as Error).message || 'Failed to delete option');
    }
  };

  const handle_delete_default_option = async (item: AdminDefaultOption) => {
    const confirmed = window.confirm(`Delete default option "${item.title.en}"?`);
    if (!confirmed) {
      return;
    }
    try {
      await deleteDefaultOption(item.id);
      await refresh();
    } catch (err) {
      setError((err as Error).message || 'Failed to delete default option');
    }
  };

  const submit_section = async (input: SectionInput) => {
    if (sectionEditing?.mode === 'edit') {
      await updateSection(sectionEditing.section.id, input);
    } else {
      await createSection(projectId, input);
    }
    setSectionEditing(null);
    await refresh();
  };

  const submit_option = async (input: OptionInput) => {
    if (optionEditing?.mode === 'edit') {
      await updateOption(optionEditing.option.id, input);
    } else if (optionEditing?.mode === 'new') {
      await createOption(projectId, optionEditing.sectionId, input);
    }
    setOptionEditing(null);
    await refresh();
  };

  const submit_default_option = async (input: DefaultOptionInput) => {
    if (defaultOptionEditing?.mode === 'edit') {
      await updateDefaultOption(defaultOptionEditing.defaultOption.id, input);
    } else {
      await createDefaultOption(projectId, input);
    }
    setDefaultOptionEditing(null);
    await refresh();
  };

  if (isLoading) {
    return <p className={styles.muted}>Loading project…</p>;
  }

  if (!detail) {
    return (
      <>
        <button type="button" className={styles.back_link} onClick={onBack}>
          ← Back to projects
        </button>
        <p className={styles.error_text}>{error ?? 'Project not found'}</p>
      </>
    );
  }

  const price_label = (option: AdminOption | AdminDefaultOption): string =>
    option.price ? `$${option.price.us}` : '—';

  return (
    <>
      <button type="button" className={styles.back_link} onClick={onBack}>
        ← Back to projects
      </button>

      {error && <p className={styles.error_text}>{error}</p>}

      <div className={styles.card}>
        <h2 className={styles.card_title}>Project</h2>
        <form onSubmit={handle_rename}>
          <div className={styles.form_grid}>
            <div className={styles.field}>
              <span className={styles.field_label}>Company name</span>
              <input
                className={styles.input}
                value={companyName}
                onChange={(event) => setCompanyName(event.target.value)}
              />
            </div>
          </div>
          <div className={styles.actions_row}>
            <button type="submit" className={styles.button_primary} disabled={isRenaming}>
              {isRenaming ? 'Saving…' : 'Save name'}
            </button>
          </div>
        </form>
      </div>

      <div className={styles.card}>
        <h2 className={styles.card_title}>Client account</h2>
        <form onSubmit={handle_save_client}>
          <div className={styles.form_grid}>
            <div className={styles.field}>
              <span className={styles.field_label}>Username</span>
              <input
                className={styles.input}
                value={clientUsername}
                autoComplete="off"
                onChange={(event) => setClientUsername(event.target.value)}
              />
            </div>
            <div className={styles.field}>
              <span className={styles.field_label}>
                New password (leave empty to keep current)
              </span>
              <input
                className={styles.input}
                type="password"
                autoComplete="new-password"
                value={clientPassword}
                onChange={(event) => setClientPassword(event.target.value)}
              />
            </div>
          </div>

          {clientError && <p className={styles.error_text}>{clientError}</p>}
          {clientMessage && <p className={styles.muted}>{clientMessage}</p>}

          <div className={styles.actions_row}>
            <button
              type="submit"
              className={styles.button_primary}
              disabled={isSavingClient}
            >
              {isSavingClient ? 'Saving…' : 'Save client'}
            </button>
          </div>
        </form>
      </div>

      <div className={styles.card}>
        <h2 className={styles.card_title}>Sections &amp; options</h2>

        {detail.sections.length === 0 && !sectionEditing && (
          <p className={styles.muted}>No sections yet.</p>
        )}

        {detail.sections.map((section) => {
          const section_options = detail.options.filter(
            (option) => option.section_id === section.id
          );

          return (
            <div key={section.id} className={styles.section_block}>
              <div className={styles.section_block_header}>
                <div>
                  <h3 className={styles.section_block_title}>
                    {section.title[Locale.en] || section.title[Locale.ru]}
                  </h3>
                  <span className={styles.muted}>
                    {section.optional ? 'Optional' : 'Required'} ·{' '}
                    {section.options_num
                      ? `max ${section.options_num} selection(s)`
                      : 'unlimited selections'}
                  </span>
                </div>
                <div className={styles.actions_row} style={{ marginTop: 0 }}>
                  <button
                    type="button"
                    className={`${styles.button_secondary} ${styles.button_small}`}
                    onClick={() => setSectionEditing({ mode: 'edit', section })}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className={`${styles.button_secondary} ${styles.button_small}`}
                    onClick={() => setOptionEditing({ mode: 'new', sectionId: section.id })}
                  >
                    Add option
                  </button>
                  <button
                    type="button"
                    className={`${styles.button_danger} ${styles.button_small}`}
                    onClick={() => handle_delete_section(section)}
                  >
                    Delete
                  </button>
                </div>
              </div>

              {sectionEditing?.mode === 'edit' &&
                sectionEditing.section.id === section.id && (
                  <SectionForm
                    initial={sectionEditing.section}
                    onSubmit={submit_section}
                    onCancel={() => setSectionEditing(null)}
                  />
                )}

              {optionEditing?.mode === 'new' && optionEditing.sectionId === section.id && (
                <OptionForm
                  onSubmit={submit_option}
                  onCancel={() => setOptionEditing(null)}
                />
              )}

              {section_options.length === 0 && (
                <p className={styles.muted}>No options in this section.</p>
              )}

              {section_options.map((option) => (
                <React.Fragment key={option.id}>
                  <div className={styles.option_row}>
                    <span>
                      {option.title[Locale.en] || option.title[Locale.ru]}{' '}
                      <span className={styles.muted}>
                        {price_label(option)}
                        {option.is_per_hour ? ' /hour' : ''}
                      </span>
                    </span>
                    <span className={styles.actions_row} style={{ marginTop: 0 }}>
                      <button
                        type="button"
                        className={`${styles.button_secondary} ${styles.button_small}`}
                        onClick={() => setOptionEditing({ mode: 'edit', option })}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className={`${styles.button_danger} ${styles.button_small}`}
                        onClick={() => handle_delete_option(option)}
                      >
                        Delete
                      </button>
                    </span>
                  </div>
                  {optionEditing?.mode === 'edit' &&
                    optionEditing.option.id === option.id && (
                      <OptionForm
                        initial={optionEditing.option}
                        onSubmit={submit_option}
                        onCancel={() => setOptionEditing(null)}
                      />
                    )}
                </React.Fragment>
              ))}
            </div>
          );
        })}

        {sectionEditing?.mode === 'new' ? (
          <SectionForm
            onSubmit={submit_section}
            onCancel={() => setSectionEditing(null)}
          />
        ) : (
          <div className={styles.actions_row}>
            <button
              type="button"
              className={styles.button_primary}
              onClick={() => setSectionEditing({ mode: 'new' })}
            >
              Add section
            </button>
          </div>
        )}
      </div>

      <div className={styles.card}>
        <h2 className={styles.card_title}>Default options (always included)</h2>

        {detail.default_options.length === 0 && !defaultOptionEditing && (
          <p className={styles.muted}>No default options.</p>
        )}

        {detail.default_options.map((item) => (
          <React.Fragment key={item.id}>
            <div className={styles.option_row}>
              <span>
                {item.title[Locale.en] || item.title[Locale.ru]}{' '}
                <span className={styles.muted}>{price_label(item)}</span>
              </span>
              <span className={styles.actions_row} style={{ marginTop: 0 }}>
                <button
                  type="button"
                  className={`${styles.button_secondary} ${styles.button_small}`}
                  onClick={() =>
                    setDefaultOptionEditing({ mode: 'edit', defaultOption: item })
                  }
                >
                  Edit
                </button>
                <button
                  type="button"
                  className={`${styles.button_danger} ${styles.button_small}`}
                  onClick={() => handle_delete_default_option(item)}
                >
                  Delete
                </button>
              </span>
            </div>
            {defaultOptionEditing?.mode === 'edit' &&
              defaultOptionEditing.defaultOption.id === item.id && (
                <DefaultOptionForm
                  initial={defaultOptionEditing.defaultOption}
                  onSubmit={submit_default_option}
                  onCancel={() => setDefaultOptionEditing(null)}
                />
              )}
          </React.Fragment>
        ))}

        {defaultOptionEditing?.mode === 'new' ? (
          <DefaultOptionForm
            onSubmit={submit_default_option}
            onCancel={() => setDefaultOptionEditing(null)}
          />
        ) : (
          <div className={styles.actions_row}>
            <button
              type="button"
              className={styles.button_primary}
              onClick={() => setDefaultOptionEditing({ mode: 'new' })}
            >
              Add default option
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default ProjectDetail;
